import { InfoCircleFilled } from '@ant-design/icons'
import { Permission, SubjectType, UserPublic } from '@pollination-solutions/pollination-sdk'
import { Avatar, Button, Divider, message, Modal, notification, Select, Space, Tooltip, Typography } from 'antd'
import { useAuth } from 'auth-context'
import { useAccount, useLicenses } from 'hooks'
import debounce from 'lodash.debounce'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

interface AddOrganizationMemberProps {
  disabled?: boolean
  searchUsers: (searchTerm: string) => Promise<UserPublic[]>
  addUsers: (usernames: string[]) => Promise<void>
}

const AddOrganizationMember: React.FC<AddOrganizationMemberProps> = ({ 
  disabled, 
  searchUsers, 
  addUsers 
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<UserPublic[]>([])
  const [searchedUsers, setSearchedUsers] = useState<UserPublic[]>([])

  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [selectedPools, setSelectedPools] = useState<string[]>([])

  const { client } = useAuth()
  const { account } = useAccount()

  const upsertOrgMemberPermission = useCallback(
    async (
      poolId: string,
      users: UserPublic[], 
      permission: Permission,
      callback?: (value: any) => void
    ) => {
    const newAccessPolicies = users.map(user => ({
      permission,
      subject: {
        name: user.username,
        account_type: 'user',
        id: user.username,
        subject_type: SubjectType.User
      }
    }))

    try {
      await client.licenses.grantAccessToPool({ 
        poolId,
        licensePoolAccessPolicyList: { resources: newAccessPolicies } })
      if (callback) {
        callback(true)
      }
    } catch (error) {
      message.error('Error adding member to license pool')
    }
  }, [client])

  const { loading: loadingLicenses,
    licensePools,
  } = useLicenses()

  const debounceSearch = useMemo(() => debounce((search: string) => {
    searchUsers(search).then((users) => {
      setUsers(users)
      setSearchedUsers(prev => {
        const newUsers = users.filter(user => !prev.some(prevUser => prevUser.username === user.username))
        return [...prev, ...newUsers]
      })
    }
    ).catch((e) => {
      notification.error({ message: 'Error searching for users', description: e.message })
    })
  }, 50), [searchUsers])


  const showModal = () => {
    setIsModalVisible(true)
  }

  const handleOk = () => {
    if (selectedUsers.length > 0) {
      setLoading(true)
      addUsers(selectedUsers)
      .then(() => {
        selectedPools.forEach((poolId) => {
          const usersToAdd = searchedUsers.filter(user => selectedUsers.includes(user.username))
          upsertOrgMemberPermission(poolId, usersToAdd, Permission.Read)
        })

        setIsModalVisible(false)
        notification.success({ message: 'Members added to the organization.' })
      }).catch((e) => {
        notification.error({ message: 'Error adding members', description: e.message })
      }).finally(() => {
        setSelectedUsers([])
        setSelectedPools([])
        setSearchedUsers([]) // Reset searched users to avoid stale data in future searches
        setLoading(false)
      })
      setIsModalVisible(false)
    }
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  useEffect(() => {
    debounceSearch('')
  }, [debounceSearch])

  const accountPools = useMemo(() => {
    return licensePools.filter(p => p.owner.id === account?.id)
  }, [licensePools, account])

  // Add all license by default if there is only one license pool
  useEffect(() => {
    if (accountPools.length > 0) {
      setSelectedPools(accountPools.map(pool => pool.id))
    }
  }, [accountPools])

  return (
    <>
      <Button type="primary" onClick={showModal} disabled={disabled} >
        Add Member
      </Button>
      <Modal
        title="Add Organization Member"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="back" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" 
            type="primary" 
            onClick={handleOk} 
            loading={loading} 
            disabled={selectedUsers.length === 0}>
            Add
          </Button>,
        ]}
      >
        <Space direction='vertical' style={{
          width: '100%'
        }}>
          <Select
            mode="multiple"
            placeholder="Search for users to add" 
            value={selectedUsers}
            onSelect={(value) => {
              setSelectedUsers((prev) => [...prev, value as string])
            }}
            onDeselect={(value) => {
              setSelectedUsers((prev) => prev.filter((user) => user !== value))
            }} 
            onSearch={debounceSearch} 
            showSearch
            style={{ width: '100%' }}
          >
            {users.map((user) => (
              <Select.Option key={user.username} value={user.username}>
                <Space size='small' align='center'>
                  <Avatar src={user.picture} alt='User avatar' size={'small'} />
                  <Typography.Text>{user.name ?? user.username}</Typography.Text>
                  <Tooltip title={
                    <Space direction='vertical'>
                      <strong>{user.username}</strong>
                      <Divider style={{
                        margin: 0
                      }}/>
                      {user.description}
                    </Space>}
                  >
                    <InfoCircleFilled 
                    style={{
                      color: '#1890ff',
                    }} />
                  </Tooltip>
                </Space>
              </Select.Option>
            ))}
          </Select>
            <Typography.Text>
              Add to License Pools
            </Typography.Text>
            <Select
              loading={loadingLicenses}
              style={{ width: '100%' }}
              mode="multiple"
              placeholder="Select license pools to grant access"
              value={selectedPools}
              onChange={(value) => setSelectedPools(value)}
            >
              {accountPools.map((pool) => (
                <Select.Option key={pool.id} value={pool.id}>
                  <Typography.Text>{
                    pool.product.split('_')
                    ?.map(word => word.charAt(0)
                    ?.toUpperCase() + word.slice(1)).join(' ')}</Typography.Text>
                </Select.Option>
              ))}
            </Select>
        </Space>
      </Modal>
    </>
  )
}

export default AddOrganizationMember